import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";

/**
 * Minimal cinematic "DESCEND" scroll cue for the landing hero only.
 * - Fades in after the Triarch loader completes (window flag / event).
 * - Quietly invites the user into the next layer; smooth-scrolls to #signal.
 * - Fades + blurs away the moment the user leaves the hero; reappears subtly
 *   when scrolled back to the top.
 * Lives inside <Hero>, so it is inherently rendered on '/' only.
 */
export const HeroScrollCue = () => {
  const [revealed, setRevealed] = useState(
    typeof window !== "undefined" && window.__triarchLoaderDone === true
  );
  const [atTop, setAtTop] = useState(true);

  useEffect(() => {
    if (revealed) return undefined;
    const onDone = () => setRevealed(true);
    window.addEventListener("triarch:loader-done", onDone);
    return () => window.removeEventListener("triarch:loader-done", onDone);
  }, [revealed]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setAtTop(window.scrollY < 48);
        ticking = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const onClick = () => {
    const el = document.getElementById("signal");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    else window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  return (
    <AnimatePresence>
      {revealed && atTop && (
        <motion.button
          type="button"
          data-testid="hero-scroll-cue"
          onClick={onClick}
          aria-label="Descend to the next section"
          initial={{ opacity: 0, y: 12, filter: "blur(6px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: 16, filter: "blur(8px)" }}
          transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
          className="group absolute bottom-10 left-1/2 z-20 flex -translate-x-1/2 flex-col items-center gap-2 focus-visible:outline-none sm:bottom-8"
        >
          <span className="font-mono-accent text-[9px] uppercase tracking-[0.36em] text-white/40 transition-colors duration-300 group-hover:text-[#C9AE78]/85 sm:text-[10px]">
            Descend
          </span>
          <span className="relative flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-white/[0.02] backdrop-blur-sm transition-all duration-300 group-hover:border-[#C9AE78]/45 group-hover:shadow-[0_0_22px_rgba(203,174,120,0.28)] sm:h-11 sm:w-11">
            <span className="pointer-events-none absolute inset-x-1/2 top-2 h-2 w-px -translate-x-1/2 bg-gradient-to-b from-[#C9AE78]/40 to-transparent" />
            <ChevronDown
              className="h-4 w-4 text-white/55 transition-all duration-300 group-hover:translate-y-0.5 group-hover:text-[#C9AE78]"
              strokeWidth={1.5}
            />
          </span>
        </motion.button>
      )}
    </AnimatePresence>
  );
};
